import {Chip} from '@dropbox/dig-components/chip';
import {Split, withShade} from '@dropbox/dig-foundations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {t} from 'i18next';
import {KeyboardEvent, useEffect, useState} from 'react';

import {SearchRow} from './searchRows';

/** Today's box's filters, in its order (its i18n keys are the filter names). */
const FILTERS = ['all', 'people', 'teams', 'news', 'workstreams', 'knowledge'] as const;
type SearchFilter = (typeof FILTERS)[number];

const filterOf = (row: SearchRow): SearchFilter | null => {
  switch (row.kind) {
    case 'person':
      return 'people';
    case 'team':
      return 'teams';
    case 'news':
      return 'news';
    case 'workstream':
      return 'workstreams';
    case 'knowledge':
      return 'knowledge';
    default:
      return null;
  }
};

/**
 * Today's box's filter chips, for the search extras: once you've typed and the
 * results mix types (or a filter is on, so it can be cleared), a row of chips
 * narrows the results to one type; Tab cycles them. The Ask row always stays.
 * Logs SEARCH_FILTER_SELECTED like today's box when `log` is on.
 */
export const useSearchFilter = ({
  live,
  rows,
  enabled,
  log,
}: {
  live: string;
  rows: SearchRow[];
  enabled: boolean;
  log: boolean;
}) => {
  const [filter, setFilter] = useState<SearchFilter>('all');
  useEffect(() => {
    if (!live) setFilter('all');
  }, [live]);

  const kinds = new Set(rows.map(filterOf).filter(Boolean));
  const shown = enabled && live.length > 0 && (filter !== 'all' || kinds.size > 1);
  const visible =
    filter === 'all' ? rows : rows.filter((row) => row.kind === 'ask' || filterOf(row) === filter);

  const choose = (next: SearchFilter) => {
    if (log) {
      analyticsLogger().logEvent('SEARCH_FILTER_SELECTED', {
        filter: next,
        results: visible.filter(({kind}) => kind !== 'ask').length,
        input: live.length,
      });
    }
    setFilter(next);
  };

  /** Tab and Shift+Tab in the field move through the chips. */
  const onTab = (event: KeyboardEvent<HTMLElement>) => {
    if (!shown || event.key !== 'Tab') return false;
    event.preventDefault();
    const index = FILTERS.indexOf(filter);
    choose(FILTERS[(index + (event.shiftKey ? FILTERS.length - 1 : 1)) % FILTERS.length]);
    return true;
  };

  const chips = shown ? (
    <Split gap="8" wrap="wrap">
      {FILTERS.map((option) => (
        <Split.Item key={option}>
          <Chip
            size="small"
            isSelected={option === filter}
            onClick={() => choose(option)}
            {...withShade({})}
          >
            {t(option)}
          </Chip>
        </Split.Item>
      ))}
    </Split>
  ) : null;

  return {visible, chips, onTab};
};
